import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, thumb } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Instagram, Globe, Heart, ArrowRight } from "lucide-react";
import ImageUpload from "../components/ImageUpload";

export function CreatorsList() {
  const [creators, setCreators] = useState([]);
  useEffect(() => { api.get("/creators").then(({ data }) => setCreators(data)); }, []);
  return (
    <div data-testid="creators-page" className="pt-32 pb-24 bg-[#F2E9D8] fade-in">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4">Our community of teachers</p>
        <h1 className="font-serif font-black text-5xl sm:text-7xl text-[#121212] leading-[0.95]">
          Meet the <span className="lime-swipe italic">makers</span>.
        </h1>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {creators.length === 0 ? <p className="text-[#121212]/60">No creators yet.</p> :
            creators.map((c) => (
              <Link key={c.id} to={`/creators/${c.id}`} data-testid={`creator-${c.id}`}
                className="group bg-white border-2 border-[#121212] hover:bg-[#C6FF33] transition-all">
                <div className="aspect-square overflow-hidden border-b-2 border-[#121212] bg-[#A89D8A]/20">
                  {c.photo_url ? <img src={thumb(c.photo_url, 600)} alt={c.name} className="w-full h-full object-cover" /> :
                    <div className="w-full h-full flex items-center justify-center font-serif font-black text-6xl text-[#121212]/40">{c.name[0]}</div>}
                </div>
                <div className="p-6">
                  <h3 className="font-serif font-black text-3xl text-[#121212]">{c.name}</h3>
                  {c.specialties?.length > 0 && <p className="text-xs tracking-[0.2em] uppercase font-bold mt-2">{c.specialties.slice(0, 3).join(" · ")}</p>}
                  {c.bio && <p className="mt-3 text-sm text-[#121212]/80 line-clamp-3">{c.bio}</p>}
                  <p className="mt-4 text-xs tracking-[0.2em] uppercase font-bold inline-flex items-center gap-2">View profile <ArrowRight size={14} /></p>
                </div>
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
}

export function CreatorDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [following, setFollowing] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => { api.get(`/creators/${id}`).then(({ data }) => setC(data)); }, [id]);
  const toggleFollow = async () => {
    if (!user || user === false) { nav("/login?next=/creators/" + id); return; }
    try {
      const { data } = await api.post(`/creators/${id}/follow`);
      setFollowing(data.following);
      toast.success(data.following ? "Following!" : "Unfollowed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!c) return <div className="pt-32 px-6 text-[#121212]/60">Loading…</div>;
  return (
    <div data-testid="creator-detail-page" className="pt-32 pb-24 bg-[#F2E9D8] fade-in">
      <div className="max-w-5xl mx-auto px-6 sm:px-12 lg:px-16 grid lg:grid-cols-2 gap-12">
        <div className="aspect-square overflow-hidden border-2 border-[#121212] bg-[#A89D8A]/20">
          {c.photo_url ? <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" /> :
            <div className="w-full h-full flex items-center justify-center font-serif font-black text-9xl text-[#121212]/40">{c.name[0]}</div>}
        </div>
        <div>
          <p className="text-xs tracking-[0.3em] uppercase font-bold">Creator</p>
          <h1 className="font-serif font-black text-6xl text-[#121212] mt-3 leading-[0.95]">{c.name}</h1>
          <p className="mt-4 text-sm">{c.followers_count} {c.followers_count === 1 ? "follower" : "followers"}</p>
          {c.bio && <p className="mt-6 text-[#121212]/80 leading-relaxed">{c.bio}</p>}
          {c.specialties?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {c.specialties.map(s => <span key={s} className="px-3 py-1 bg-[#C6FF33] text-xs tracking-[0.2em] uppercase font-bold border-2 border-[#121212]">{s}</span>)}
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            {c.instagram && <a href={`https://instagram.com/${c.instagram.replace("@","")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#121212] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#121212] hover:text-[#F2E9D8]"><Instagram size={14} /> Instagram</a>}
            {c.website && <a href={c.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#121212] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#121212] hover:text-[#F2E9D8]"><Globe size={14} /> Website</a>}
          </div>
          <button data-testid="follow-btn" onClick={toggleFollow} className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold">
            <Heart size={14} fill={following ? "#C6FF33" : "none"} /> {following ? "Following" : "Follow"}
          </button>
        </div>
      </div>
      {c.upcoming_classes?.length > 0 && (
        <div className="max-w-5xl mx-auto px-6 sm:px-12 lg:px-16 mt-20">
          <h2 className="font-serif font-black text-4xl text-[#121212]">Upcoming <span className="lime-swipe italic">classes</span></h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {c.upcoming_classes.map((k) => (
              <Link key={k.id} to={`/classes/${k.slug || k.id}`} data-testid={`upcoming-${k.id}`} className="group bg-white border-2 border-[#121212] hover:bg-[#C6FF33] transition-all flex">
                <img src={thumb(k.image_url, 300)} alt="" className="w-32 h-32 object-cover border-r-2 border-[#121212]" />
                <div className="p-4 flex-1">
                  <p className="text-xs tracking-[0.2em] uppercase font-bold">{k.date}</p>
                  <h3 className="font-serif font-black text-xl mt-1">{k.title}</h3>
                  <p className="font-serif font-black text-2xl mt-2">${k.price}</p>
                </div>
              </Link>
            ))}
          </div>
          <Link to="/classes" data-testid="book-class-cta" className="mt-8 inline-flex items-center gap-3 px-8 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold">
            Book a class on Cake Society <ArrowRight size={16} />
          </Link>
        </div>
      )}
    </div>
  );
}

export function CreatorEdit() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ bio: "", photo_url: "", instagram: "", website: "", specialties: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!user || user === false || user.role !== "creator") return;
    api.get(`/creators/${user.id}`).then(({ data }) =>
      setProfile({ ...data, specialties: (data.specialties || []).join(", ") })).catch(() => {});
  }, [user]);
  if (!user || user === false) return null;
  if (user.role !== "creator") return <div className="pt-32 px-6 text-[#121212]/60 max-w-3xl mx-auto">Only creator accounts can edit a public profile. Register as a creator to teach with us.</div>;
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...profile, specialties: profile.specialties.split(",").map(s => s.trim()).filter(Boolean) };
      await api.put("/creators/me", payload);
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const F = (k, l, opts={}) => (
    <div className="mb-4"><label className="text-xs tracking-[0.2em] uppercase font-bold block mb-2">{l}</label>
      {opts.area ?
        <textarea data-testid={`profile-${k}`} value={profile[k] || ""} onChange={e => setProfile({...profile, [k]: e.target.value})} rows={4} className="w-full px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none" /> :
        <input data-testid={`profile-${k}`} value={profile[k] || ""} onChange={e => setProfile({...profile, [k]: e.target.value})} placeholder={opts.placeholder} className="w-full px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none" />}
    </div>
  );
  return (
    <div data-testid="creator-edit-page" className="pt-32 pb-24 bg-[#F2E9D8] fade-in">
      <div className="max-w-2xl mx-auto px-6">
        <h1 className="font-serif font-black text-5xl text-[#121212]">Your <span className="lime-swipe italic">profile</span></h1>
        <p className="mt-3 text-[#121212]/70">Public details visible on your creator page.</p>
        <div className="mt-10">
          <div className="mb-4">
            <ImageUpload label="Profile photo" testid="profile-photo" value={profile.photo_url || ""} onChange={(url) => setProfile({ ...profile, photo_url: url })} />
          </div>
          {F("bio", "Bio", { area: true })}
          {F("instagram", "Instagram handle", { placeholder: "@yourname" })}
          {F("website", "Website", { placeholder: "https://…" })}
          {F("specialties", "Specialties (comma-separated)", { placeholder: "Buttercream, Fondant, Wedding cakes" })}
          <button data-testid="save-profile-btn" onClick={save} disabled={busy} className="px-8 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold disabled:opacity-40">{busy ? "Saving…" : "Save profile"}</button>
        </div>
      </div>
    </div>
  );
}
